import { SubscriptionNoticeComponent } from './subscription-notice.component';
import { SystemNoticeState } from './system-notice-state';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { OnboardingContextoComponent } from '@core/onboarding/onboarding-contexto.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { NgxPermissionsService } from 'ngx-permissions';
import { ApiService } from '@core/services/api/api.service';
import { AuthService } from '@core/services/common/auth.service';
import { TenancyService } from '@core/services/common';
import { segundoFatorSatisfeito } from '@model/two-factor-current-method.model';
import { SupportIncident, ordenarIncidentesPorGravidade } from '@model/support';
import { SupportIncidentBannerComponent } from '@shared/components/support/incident-banner/support-incident-banner.component';
import { SupportService } from 'src/app/views/pages/tenant/support/services';
import { AsideComponent } from './aside/aside.component';
import { HeaderComponent } from './header/header.component';
import { SupportHubComponent } from './support-hub/support-hub.component';
import { RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';

/** Intervalo do reconsulta do status (D-HD37): a faixa não é tempo real, mas some sozinha quando o incidente é resolvido. */
const INTERVALO_STATUS_MS = 5 * 60 * 1000;

/**
 * A casca do sistema — o que o `kt-base` do Metronic era, em código nosso.
 *
 * Ela é o componente de rota de `/painel` e de `/admin` (ver
 * `tenant-routing.module.ts` e `central-routing.module.ts`) e faz três coisas:
 *
 *  1. **carrega as permissões da sessão** no `NgxPermissionsService` e as
 *     descarrega ao sair. É a única responsabilidade do `kt-base` que não era
 *     decoração de tema, e a que 63 templates consomem;
 *  2. decide o MODO da gaveta (`side` no desktop, `over` no mobile) pelo
 *     `BreakpointObserver` do CDK e guarda o recolhimento (modo ícone) do
 *     menu. Quem guarda aberto/fechado é o próprio `mat-sidenav` — abrir,
 *     fechar, backdrop, `Esc` e foco são dele (Onda 3, Task 8);
 *  3. desenha `mat-sidenav-container` (aside + barra superior +
 *     `<router-outlet>`) e o botão de voltar ao topo.
 *
 * **O que ela deliberadamente NÃO faz**, e cada linha aqui é uma medição do
 * shell antigo, não uma opinião:
 *
 *  - **não escreve classe no `<body>`.** O `HtmlClassService` existia para
 *    alimentar o CSS do Metronic (`header-fixed`, `aside-enabled`,
 *    `brand-dark`…). O layout agora é do componente;
 *  - **não carrega `LayoutConfig`/`PageConfig`.** O `LayoutConfigService` já é
 *    carregado no `APP_INITIALIZER` (`app.module.ts`), e o `PageConfigService`
 *    só era lido pelo subheader do tema, que não renderiza nada — o template
 *    dele está inteiro dentro de um comentário;
 *  - **não toca no `RenderCountService`.** Medido: quem incrementa são o
 *    dashboard e as duas telas de contas, no `ngOnInit` delas; o shell nunca
 *    leu nem escreveu aquele contador. Incrementar aqui trocaria a classe
 *    `filter-top` de três telas em silêncio;
 *  - **não decide breakpoint por media query própria.** A media query de
 *    991.98px (o `lg` do Bootstrap menos 0,02, fóssil do Metronic) saiu na
 *    Task 8; 32 outros arquivos do projeto já resolviam responsividade pelo
 *    CDK.
 *
 * `carregarPermissoes` nasceu PORTE LITERAL do `kt-base`, com quatro
 * defeitos pinados em `layout.component.spec.ts` (BUG-CLI-340, corrigido):
 * sem cancelar a própria inscrição, sem tratar erro, e sem estado de
 * carregamento para o primeiro consumidor síncrono. Ver o comentário do
 * método.
 */
@Component({
  selector: 'mg-layout',
  providers: [SystemNoticeState],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  // CV-CLI-03 — convertido em 29/08/2026, e as duas metades foram MEDIDAS.
  //
  // O estado PRÓPRIO desta casca é seguro sob `OnPush`: `asideRecolhido` e
  // `mostrarTopo` mudam por evento do próprio template (`(alternarRecolhido)`,
  // `(click)`) e pelo `markForCheck()` do `aoRolar`; `modoGaveta` muda na
  // inscrição do `BreakpointObserver`, que NÃO nasce do template — por isso o
  // `markForCheck()` explícito na inscrição, afirmado em
  // `layout.deteccao.spec.ts` com esta casca montada DENTRO de um hospedeiro
  // `OnPush` (montada como raiz, o congelamento não apareceria). O estado de
  // aberto/fechado da gaveta saiu daqui na Task 8: ele é do `mat-sidenav`,
  // que tem detecção própria.
  //
  // O que travava não era o estado próprio, era um DESCENDENTE: o
  // `DashboardComponent` era `Eager` e nada marcava a view dele, então ele
  // dependia da travessia global descer por `AppComponent` → `TenantComponent`
  // → esta casca. Isso acabou: o `isFetching` do swr virou `signal`
  // (`swr/services/internal/pipe.ts`) e o painel se marca sozinho — ver
  // `dashboard.deteccao.spec.ts`.
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OnboardingContextoComponent, AsideComponent, HeaderComponent, RouterOutlet, AsyncPipe, MatIconButton,
    MatSidenavModule, SupportIncidentBannerComponent, SupportHubComponent, SubscriptionNoticeComponent,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly apiService = inject(ApiService);
  private readonly permissoes = inject(NgxPermissionsService);
  private readonly autenticacao = inject(AuthService);
  private readonly zona = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly tenancy = inject(TenancyService);
  private readonly suporte = inject(SupportService);
  private readonly systemNotices = inject(SystemNoticeState);
  /** Quem decide qual aviso ocupa o topo (prioridade única, ver `SystemNoticeState`). */
  protected readonly avisos = this.systemNotices;

  @ViewChild('gaveta') private readonly gaveta?: MatSidenav;

  /** Mesmo critério do cabeçalho e do menu lateral (`aside`). */
  protected readonly ehTenant = this.tenancy.isSubdomain;

  /** Faixa de incidente (D-HD37, spec §6.7): só existe no tenant, e só enquanto houver incidente ativo. */
  protected readonly incidentesAtivos = signal<SupportIncident[]>([]);

  /**
   * Substitui a media query de 991.98px do `aside.component.scss` — que era o
   * breakpoint `lg` do Bootstrap menos 0,02, um fóssil do Metronic. 32 outros
   * arquivos do projeto já resolviam responsividade pelo CDK.
   */
  readonly ehMobile$ = this.breakpoints.observe([Breakpoints.Handset, Breakpoints.TabletPortrait]).pipe(
    map(estado => estado.matches),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** `side` empurra o conteúdo (desktop); `over` cobre com backdrop (mobile). */
  modoGaveta: 'over' | 'side' = 'side';

  /** Menu em modo ícone — só tem efeito no desktop. */
  asideRecolhido = false;

  /** O botão de voltar ao topo só existe depois de a página ter rolado. */
  mostrarTopo = false;

  private static readonly ROLAGEM_PARA_MOSTRAR_TOPO = 300;

  private readonly inscricoes = new Subscription();

  manutencaoAtiva = false;

  ngOnInit(): void {
    this.systemNotices.maintenanceActive.set(this.manutencaoAtiva);
    this.carregarPermissoes();

    if (this.ehTenant) {
      // Silenciosa de propósito (D-HD37 não promete tempo real): erro de
      // rede não pode acender nem apagar a faixa por conta própria, e a
      // linha de status da central de suporte já cobre o estado de falha.
      this.inscricoes.add(
        timer(0, INTERVALO_STATUS_MS).pipe(
          switchMap(() => this.suporte.status().pipe(catchError(() => EMPTY))),
        ).subscribe(status => {
          this.incidentesAtivos.set(ordenarIncidentesPorGravidade(status.active));
          this.systemNotices.update(status.active.length);
          this.cdr.markForCheck();
        })
      );
    }

    this.inscricoes.add(
      this.ehMobile$.subscribe(mobile => {
        this.modoGaveta = mobile ? 'over' : 'side';
        // A emissão do BreakpointObserver não é evento de template desta view:
        // sob `OnPush`, sem esta marcação a gaveta ficaria no modo antigo até
        // a próxima travessia — pinado em `layout.deteccao.spec.ts`.
        this.cdr.markForCheck();
      })
    );

    // FORA da zona, como o `KTScrolltop` do shell antigo: um `window:scroll`
    // via `@HostListener` roda dentro da zona e custa um `tick()` na árvore
    // inteira a ~60 eventos/segundo ao rolar uma tabela de 45 colunas — e no
    // ng22 o tick do listener vem TODO da zona (o scheduler ignora
    // `notify(Listener)` em app com zone). Só a TRANSIÇÃO de estado reentra:
    // duas vezes por rolagem, não sessenta por segundo.
    this.zona.runOutsideAngular(() => window.addEventListener('scroll', this.aoRolar, { passive: true }));
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.aoRolar);
    this.inscricoes.unsubscribe();
    this.permissoes.flushPermissions();
  }

  /**
   * O menu navegou e pediu para sair da frente. Só faz sentido na gaveta
   * `over`: no desktop (`side`) a sidebar é permanente e fechá-la a cada
   * navegação apagaria o menu da tela — o template do plano (`gaveta.close()`
   * direto) tinha exatamente esse defeito.
   */
  fecharNoMobile(): void {
    if (this.modoGaveta === 'over') {
      this.gaveta?.close();
    }
  }

  alternarRecolhido(): void {
    this.asideRecolhido = !this.asideRecolhido;
  }

  /**
   * Arrow de propósito: é a MESMA referência no `addEventListener` e no
   * `removeEventListener` — método comum re-`bind`ado registraria um ouvinte
   * que o destroy não sabe remover.
   */
  private readonly aoRolar = (): void => {
    const mostrar = window.scrollY > LayoutComponent.ROLAGEM_PARA_MOSTRAR_TOPO;

    if (mostrar === this.mostrarTopo) {
      return;
    }

    this.zona.run(() => {
      this.mostrarTopo = mostrar;
      this.cdr.markForCheck();
    });
  };

  subirAoTopo(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * BUG-CLI-340 — porte literal do `kt-base`, com quatro defeitos:
   *
   *  1. sem cancelar a própria inscrição, uma casca destruída antes da
   *     resposta chegar aplicava a permissão da sessão ANTERIOR sobre o
   *     store, mesmo sem ninguém mais lendo dele;
   *  2. o mesmo defeito, mão inversa: a resposta atrasada de uma sessão
   *     (ex.: administrador) vazava para dentro do store da sessão
   *     SEGUINTE (ex.: operador que entrou depois);
   *  3. sem tratamento de erro, uma falha na chamada deixava a permissão
   *     da sessão anterior de pé — `flushPermissions()` só rodava dentro
   *     do callback de SUCESSO;
   *  4. publicação assíncrona sem estado de carregamento: o primeiro
   *     consumidor síncrono (`PermissionService.checkPermissions`, que
   *     `new-payment` e `users-list` chamam antes de qualquer `isLoading`)
   *     lia o store vazio e negava tudo até a resposta chegar.
   *
   * A correção: a inscrição entra em `this.inscricoes` (cancelável no
   * `ngOnDestroy`, resolve 1 e 2), erro também esvazia o store (resolve 3),
   * e o carregamento nasce com `'*'` — liberado — até a resposta REAL
   * substituí-lo ou uma falha limpar (resolve 4). O `'*'` nunca sobrevive à
   * resposta: sucesso ou erro sempre passam por `flushPermissions()`.
   */
  private carregarPermissoes(): void {
    this.permissoes.addPermission('*');

    this.inscricoes.add(
      this.autenticacao.twoFactorMethodObserver
        .pipe(
          // ESPERA o segundo fator antes de pedir as permissões da sessão —
          // mas só quando HÁ segundo fator a esperar.
          //
          // Por que o filtro existe: `current-session.user.permissions` está no
          // grupo `two_factor` da API e responde 401 antes do segundo fator
          // (BUG-2FA-SESSION-USER-GATE-01, 28/08/2026, que fechou a exceção
          // nominal que devolvia 200-vazio); o interceptor trata TODO 401 com
          // token como sessão expirada — desloga e joga no login. Sem o filtro,
          // quem estava preso na tela de CADASTRO do 2FA (que vive DENTRO deste
          // shell) era deslogado antes de ver o QR Code.
          //
          // ⚠️ SEGUNDO FATOR OPT-IN (decisão do dono, 02/09/2026) — por que a
          // condição deixou de ser `is_authenticated === true` sozinho. Com o
          // 2FA opcional, `is_authenticated` fica `false` PARA SEMPRE em quem
          // nunca cadastrou fator: o filtro nunca deixaria passar, a resposta
          // real nunca chegaria, e o `'*'` que `carregarPermissoes()` semeia
          // logo acima como curinga de carregamento NUNCA seria substituído.
          // Resultado: a interface inteira liberada para todo mundo, em
          // silêncio, porque `flushPermissions()` só roda no `next`/`error`
          // desta inscrição. Ou seja: fazer o 2FA opcional sem mexer aqui
          // DESLIGA o gate de permissão da interface.
          //
          // A condição correta é "passou pelo segundo fator OU não há segundo
          // fator a passar" — que é `segundoFatorSatisfeito`, o mesmo predicado
          // do `TwoFactorGuard` e o espelho do `TwoFactorService::isSatisfied()`
          // da API. Quem TEM fator e ainda não o satisfez continua barrado
          // aqui, que é o que impede o logout em laço.
          //
          // O subject é reescrito quando o cadastro é concluído
          // (`TwoFactorService`), então a emissão nova dispara a carga real sem
          // recarregar a página.
          filter(metodo => segundoFatorSatisfeito(metodo)),
          switchMap(() => this.apiService.currentSession.permissions())
        )
        .subscribe({
          next: permissoes => {
            this.permissoes.flushPermissions();
            if (Array.isArray(permissoes)) {
              this.permissoes.addPermission(permissoes);
            }
          },
          error: () => {
            this.permissoes.flushPermissions();
          },
        })
    );
  }
}
